import { createContext, useContext, useState, useEffect } from "react";
import {
  fetchAnimeList,
  fetchAnimeDetails,
  fetchAnimeReviews,
} from "../services/api";
import { useAuth } from "./AuthContext";

const AnimeContext = createContext();

export function AnimeProvider({ children }) {
  const { user } = useAuth();
  const [animeList, setAnimeList] = useState([]);
  const [selectedAnime, setSelectedAnime] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Load anime list on mount
  useEffect(() => {
    async function loadAnime() {
      setLoading(true);
      try {
        const data = await fetchAnimeList();
        setAnimeList(data);
      } catch (err) {
        setError("Error fetching anime list.");
      } finally {
        setLoading(false);
      }
    }
    loadAnime();
  }, []);

  // Load user's favorites from backend
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user || !user.id) {
        setFavorites([]);
        return;
      }

      try {
        const res = await fetch("/api/favorites/" + user.id, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to fetch favorites");
        const data = await res.json();

        // Normalize DB field names to match frontend
        const formatted = (Array.isArray(data) ? data : []).map((fav) => ({
          userId: fav.user_id,
          // normalize to NUMBER for consistent comparisons
          animeId: Number(fav.animeId ?? fav.AnimeID ?? fav.anime_id),
        }));

        setFavorites(formatted.filter((f) => Number.isFinite(f.animeId) && f.animeId > 0));
      } catch (err) {
        console.error("Error loading favorites:", err);
        setFavorites([]);
      }
    };

    loadFavorites();
  }, [user]);

  const getAnimeDetails = async (id) => {
    setDetailsLoading(true);
    try {
      const data = await fetchAnimeDetails(id);
      setSelectedAnime(data);
    } catch (err) {
      setError("Error fetching anime details.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const getAnimeReviews = async (animeId) => {
    setReviewsLoading(true);
    try {
      const data = await fetchAnimeReviews(animeId);
      setReviews(data);
    } catch (err) {
      setError("Error fetching reviews.");
    } finally {
      setReviewsLoading(false);
    }
  };

  // Add to favorites
  const addToFavorites = async (anime) => {
    const rawId =
      anime?.AnimeID ??
      anime?.anime_id ??
      anime?.id ??
      anime?.AnimeId ??
      anime?.animeId ??
      null;

    const animeId = Number(rawId);

    // must be a positive integer (matches your backend validation message)
    if (!user || !user.id || !Number.isFinite(animeId) || animeId <= 0) {
      return;
    }

    try {
      const res = await fetch("/api/favorites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          // backend expects animeId (camelCase)
          userId: user.id,
          animeId,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to add favorite");
      }

      setFavorites((prev) => {
        if (prev.some((fav) => Number(fav.animeId) === animeId)) return prev;
        return [...prev, { userId: user.id, animeId }];
      });
    } catch (err) {
      console.error("Add to favorites error:", err.message);
    }
  };

  // Remove from favorites
  const removeFromFavorites = async (animeId) => {
    const numericId = Number(animeId);

    if (!user || !user.id || !Number.isFinite(numericId) || numericId <= 0) {
      return;
    }

    try {
      const res = await fetch("/api/favorites", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          // backend expects animeId (camelCase)
          userId: user.id,
          animeId: numericId,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to remove favorite");
      }

      setFavorites((prev) => prev.filter((fav) => Number(fav.animeId) !== numericId));
    } catch (err) {
      console.error("Remove from favorites error:", err.message);
    }
  };

  // Check if anime is favorited
  const isAnimeFavorited = (animeId) =>
    favorites.some((fav) => Number(fav.animeId) === Number(animeId));

  // Update anime rating
  const updateRating = async (animeId, rating) => {
    if (!user || !user.id) {
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";
      // First, check if user has already reviewed this anime
      const checkRes = await fetch(`${API_BASE}/reviews/user/${user.id}`, {
        credentials: "include",
      });

      if (!checkRes.ok) throw new Error("Failed to fetch user reviews");

      const userReviews = await checkRes.json();
      const existingReview = userReviews.find((r) => r.anime_id === animeId);

      let res;
      if (existingReview) {
        // Update existing review
        res = await fetch(`${API_BASE}/reviews/${existingReview.review_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            rating: rating,
            review_text: existingReview.review_text || "No review text",
          }),
        });
      } else {
        // Create new review
        res = await fetch(`${API_BASE}/reviews`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            anime_id: animeId,
            rating: rating,
          }),
        });
      }

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Rating update failed:", errorData);
        throw new Error("Failed to update rating");
      }

      // Fetch updated anime data to get actual average rating
      const animeRes = await fetch("/api/anime/" + animeId);
      if (animeRes.ok) {
        const updatedAnime = await animeRes.json();
        const updatedList = animeList.map((anime) =>
          anime.AnimeID === animeId
            ? { ...anime, AverageRating: updatedAnime.AverageRating }
            : anime
        );
        setAnimeList(updatedList);
      }
    } catch (err) {
      console.error("Update rating error:", err.message);
    }
  };

  return (
    <AnimeContext.Provider
      value={{
        animeList,
        selectedAnime,
        reviews,
        getAnimeDetails,
        getAnimeReviews,
        addToFavorites,
        removeFromFavorites,
        isAnimeFavorited,
        updateRating,
        favorites,
        error,
        loading,
        detailsLoading,
        reviewsLoading,
      }}
    >
      {children}
    </AnimeContext.Provider>
  );
}

export function useAnime() {
  return useContext(AnimeContext);
}

